import React, { useEffect, useState } from "react";
import ModalShell from "./ModalShell";
import { ViewAllRowsSkeleton } from "../Skeleton";
import styles from "./ViewAllModal.module.scss";

export interface ViewAllPage {
  rows: any[];
  total: number;
  lastPage: number;
  currentPage: number;
}

interface ViewAllModalProps {
  title: string;
  subtitle?: string;
  onClose: () => void;
  /** Fetch a single page (1-based). Must be memoized (useCallback) by the caller. */
  fetchPage: (page: number) => Promise<ViewAllPage>;
  /** Render the current page's rows (caller owns the markup/columns). */
  renderContent: (rows: any[]) => React.ReactNode;
  /** Loading placeholder — pass one shaped like the widget's own rows.
   *  Defaults to the list-style rows used by the teacher/student widgets. */
  skeleton?: React.ReactNode;
  maxWidth?: number;
}

/**
 * Compact list of page numbers with ellipses, e.g. 1 … 4 [5] 6 … 101.
 * Keeps the pager usable even with 100+ pages.
 */
function pageWindow(current: number, last: number): (number | "…")[] {
  if (last <= 7) return Array.from({ length: last }, (_, i) => i + 1);
  const out: (number | "…")[] = [];
  const left = Math.max(2, current - 1);
  const right = Math.min(last - 1, current + 1);
  out.push(1);
  if (left > 2) out.push("…");
  for (let i = left; i <= right; i++) out.push(i);
  if (right < last - 1) out.push("…");
  out.push(last);
  return out;
}

/**
 * Generic "view all" modal with server-side pagination (Prev / numbered / Next).
 * The page size is decided by the caller's `fetchPage` (5 for head-office
 * widgets). Keeps the shell/paging identical across widgets while letting each
 * one render its own row layout.
 */
export default function ViewAllModal({
  title,
  subtitle,
  onClose,
  fetchPage,
  renderContent,
  skeleton,
  maxWidth = 720,
}: ViewAllModalProps) {
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    fetchPage(page)
      .then((r) => {
        if (!alive) return;
        setRows(r.rows || []);
        setTotal(r.total || 0);
        setLastPage(Math.max(1, r.lastPage || 1));
      })
      .catch(() => {
        if (alive) setError(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [page, fetchPage]);

  const footer = (
    <>
      <span className={styles.count}>
        {total > 0 ? `Page ${page} of ${lastPage} · ${total} total` : " "}
      </span>
      <div className={styles.pager}>
        <button
          type="button"
          className={styles.pageBtn}
          disabled={loading || page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          Prev
        </button>
        {pageWindow(page, lastPage).map((n, i) =>
          n === "…" ? (
            <span key={`gap-${i}`} className={styles.ellipsis}>
              …
            </span>
          ) : (
            <button
              type="button"
              key={n}
              className={`${styles.pageNum} ${
                n === page ? styles.pageNumActive : ""
              }`}
              disabled={loading}
              onClick={() => setPage(n as number)}
            >
              {n}
            </button>
          ),
        )}
        <button
          type="button"
          className={styles.pageBtn}
          disabled={loading || page >= lastPage}
          onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
        >
          Next
        </button>
      </div>
    </>
  );

  return (
    <ModalShell
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      maxWidth={maxWidth}
      footer={footer}
    >
      {loading ? (
        (skeleton ?? <ViewAllRowsSkeleton />)
      ) : error ? (
        <div className={styles.state}>Couldn&apos;t load this page. Please try again.</div>
      ) : rows.length === 0 ? (
        <div className={styles.state}>No campuses to show.</div>
      ) : (
        <div className={styles.scroll}>{renderContent(rows)}</div>
      )}
    </ModalShell>
  );
}
